<script lang="ts" setup>
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import ToolbarBase from '@/components/ToolbarBase.vue'
import TextInput from '@/components/inputs/TextInput.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import SaveChangesElement from '@/components/SaveChangesElement.vue'
import GroupActions from '@/components/business/groups/GroupActions.vue'
import SuspendedBadge from '@/components/business/members/SuspendedBadge.vue'
import { useGroupForm } from '@/composables/useBusinessGroups.ts'

const props = defineProps<{
  groupId?: string
  maxParticipants?: number
  operationType: 'create' | 'edit'
}>()

const emit = defineEmits<{
  action: (type: Overlays['OverlayCreateGroup']['operationType'], data?: unknown) => void
  close: []
}>()

const { t } = useI18n()
const { smAndDown } = useDisplay()
const widgetStore = useWidgetStore()
const { members: allBusinessMembers, isLoadingMembers: isLoadingAllBusinessMembers } = useBusinessMembers()
const isValid = ref(false)

const { group, isLoading, isSaving, fetchGroup, saveGroup } = useGroupForm(() => props.groupId)

const nameError = ref<string | undefined>()

const initialGroup = {
  name: '',
  members: [] as GroupMember[],
}
const groupModel = reactive(structuredClone(initialGroup))
const originalGroup = computed(() => group.value ?? initialGroup)

const hasMoreMembers = computed(() => {
  return allBusinessMembers.value.length > groupModel.members.length
})

const isValidName = (name: unknown) => !isEmptyString(name) || t('business.groups.form.error.name_required')
const isSaveDisabled = computed(() => {
  return !isValid.value || !groupModel.members.length
})

watch(
  () => groupModel.name,
  () => {
    if (nameError.value) {
      nameError.value = undefined
    }
  }
)

const hasUnsavedChanges = computed(() => {
  return !(
    originalGroup.value.name === groupModel.name &&
    originalGroup.value.members.length === groupModel.members.length &&
    originalGroup.value.members.every((member, index) => {
      return member.id === groupModel.members[index]?.id
    })
  )
})

const { springTrap } = useNavigationTrap(hasUnsavedChanges)

const title = computed(() => {
  return props.operationType === 'create'
    ? t('business.groups.form.create_title')
    : t('business.groups.form.edit_title')
})

const displayMemberName = (member: GroupMember) => {
  if (member.firstName || member.lastName) {
    return `${member.firstName} ${member.lastName}`.trim()
  }
  return '-'
}

const canAddMoreMembers = computed(() => {
  return props.maxParticipants ? groupModel.members.length < props.maxParticipants && hasMoreMembers.value : false
})

const assignableBusinessMembers = computed(() => {
  const assignedMemberUsernames = groupModel.members.map(member => member.id)
  return allBusinessMembers.value.filter(member => !assignedMemberUsernames.includes(member.username))
})

const onAddMembers = () => {
  widgetStore.setDialog(
    'business/DialogAddMember',
    {
      assignableMembers: assignableBusinessMembers.value,
      title: t('business.groups.form.add_member.title'),
      subtitle: t('business.groups.form.add_member.subtitle'),
      cancelText: t('global.close'),
    },
    async members => {
      groupModel.members = [
        ...groupModel.members,
        ...members.map(member => ({
          ...member,
          id: member.username,
        })),
      ]
    }
  )
}

const onRemoveMember = (id: string) => {
  groupModel.members = groupModel.members.filter(member => member.id !== id)
}

const onDeleteGroup = () => {
  emit('action', 'delete', props.groupId)
}

const submit = async () => {
  const payload = {
    name: groupModel.name,
    members: groupModel.members.map(({ id }) => id),
  }
  try {
    await saveGroup(payload)
    emit('action', 'complete', { id: props.groupId, ...payload })
  } catch (error) {
    if (!isFetchError(error)) {
      widgetStore.createSnackbar({ message: t('global.general_error') })
      return
    }

    const errorCode = getErrorCode(error)
    switch (errorCode) {
      case ErrorCode.GroupNameExists:
        nameError.value = t('business.groups.form.error.name_exists')
        break
      case ErrorCode.GroupMemberLimitReached:
        widgetStore.createSnackbar({ message: t('business.groups.form.error.max_members') })
        break
      case ErrorCode.GroupLimitReached:
        widgetStore.createSnackbar({ message: t('business.groups.form.error.max_groups') })
        break
      default:
        widgetStore.createSnackbar({ message: t('global.general_error') })
    }
  }
}

const onClose = () => {
  if (hasUnsavedChanges.value) {
    void springTrap(() => {
      emit('close')
    })
  } else {
    emit('close')
  }
}
const form = useTemplateRef<{ validate: () => void } | null>('form')

if (props.operationType === 'create') {
  const unwatchForm = watchEffect(() => {
    if (form.value) {
      form.value.validate()
      unwatchForm()
    }
  })
}

const loadGroup = async () => {
  if (!props.groupId) {
    return
  }
  await fetchGroup()
  if (group.value) {
    groupModel.name = group.value.name
    groupModel.members = [...group.value.members]
  }
}

onMounted(async () => {
  try {
    await loadGroup()
  } catch {
    widgetStore.createSnackbar({ message: t('global.general_error') })
    emit('close')
  }
})
</script>

<template>
  <overlay-base>
    <template #header>
      <toolbar-base class="toolbar" :title="title" :app="false" is-closeable @close="onClose">
        <template v-if="props.operationType === 'edit'" #options>
          <group-actions :group="group" @delete="onDeleteGroup" />
        </template>
      </toolbar-base>
    </template>
    <skr-spinner v-if="isLoading" fullscreen />
    <v-container v-else class="container">
      <div class="content ma-auto px-6">
        <v-form ref="form" v-model="isValid" validate-on="eager input">
          <skr-heading level="1" class="mt-12" semantic>{{ t('business.groups.form.name_label') }}</skr-heading>
          <text-input
            v-model="groupModel.name"
            persistent-hint
            :rules="[isValidName]"
            :error-messages="nameError"
            class="mb-6 mt-10"
            data-cy="name_input"
          />
          <p class="subtitle">
            {{ t('business.groups.form.name_description_1.part_1') }}
            <span class="font-weight-bold">
              {{ t('business.groups.form.name_description_1.part_2') }}
            </span>
            {{ t('business.groups.form.name_description_2') }}
          </p>
          <skr-divider class="my-12" />

          <div class="d-flex align-center justify-space-between flex-wrap mb-6 gap-6">
            <div class="d-flex align-center">
              <skr-heading level="1" class="mb-0" semantic>
                {{ t('global.members') }}
                <span class="count"> {{ groupModel.members.length }}/{{ props.maxParticipants }} </span>
              </skr-heading>
            </div>

            <v-tooltip :disabled="hasMoreMembers" location="top" max-width="165" content-class="text-center bg-primary">
              <template #activator="{ props: activatorProps }">
                <div v-bind="activatorProps">
                  <skr-button
                    size="lg"
                    :block="smAndDown"
                    data-cy="open_add_member_dialog"
                    :disabled="isLoadingAllBusinessMembers || !canAddMoreMembers"
                    :loading="isLoadingAllBusinessMembers"
                    @click="onAddMembers"
                  >
                    {{ t('global.add') }}
                  </skr-button>
                </div>
              </template>
              <p>{{ t('business.groups.form.error.no_members_left') }}</p>
            </v-tooltip>
          </div>
        </v-form>
        <p class="subtitle">
          {{ t('business.groups.form.members_description_1.part_1') }}
          <span class="font-weight-bold">
            {{ t('business.groups.form.members_description_1.part_2') }}
          </span>
          {{ t('business.groups.form.members_description_2') }}
        </p>
        <skr-divider class="mt-8 mb-4" />
        <span v-if="!groupModel.members.length" class="error pl-3">
          {{ t('business.groups.form.error.members_required') }}
        </span>
        <div v-else>
          <div v-for="(member, index) in groupModel.members" :key="member.id" data-cy="member_list_item">
            <div class="d-flex justify-space-between py-3 align-center">
              <div class="d-flex flex-column">
                <div class="d-flex align-center ga-2">
                  <span class="name" :class="{ 'name--suspended': member.status === 'suspended' }">
                    {{ displayMemberName(member) }}
                  </span>
                  <suspended-badge v-if="member.status === 'suspended'" />
                </div>
                <span class="text-subtitle-1">{{ member.email }}</span>
              </div>
              <div :data-cy="`member_remove_${index}`">
                <v-icon
                  color="grey-dark"
                  role="button"
                  tabindex="0"
                  @click="onRemoveMember(member.id)"
                  @keydown.enter="onRemoveMember(member.id)"
                  @keydown.space.prevent="onRemoveMember(member.id)"
                  >custom:c_close</v-icon
                >
              </div>
            </div>
            <skr-divider class="my-3" />
          </div>
        </div>
      </div>
    </v-container>

    <save-changes-element
      :has-unsaved-changes="hasUnsavedChanges"
      :is-disabled="isSaveDisabled"
      :is-loading="isSaving"
      is-fixed
      @save="submit"
    >
      <template v-if="operationType === 'create'">
        <div class="action">
          <skr-button data-cy="save_button" size="xl" :disabled="isSaveDisabled" :loading="isSaving" @click="submit">
            {{ t('business.groups.form.create_title') }}
          </skr-button>
        </div>
      </template>
    </save-changes-element>
  </overlay-base>
</template>

<style lang="scss" scoped>
.container {
  max-width: 100%;
  height: calc(100% - 2 * $siteheader-height);
  overflow-y: auto;
}

.content {
  max-width: $site-width-narrow;
}

.count {
  color: $c-grey-light;
}

.subtitle {
  color: $c-grey-dark;
  font-size: 15px;
  max-width: 34em;
}

.name {
  color: $c-grey-darker;
  font-size: 1.125rem;
  font-weight: 800;

  &--suspended {
    color: $c-grey-dark;
  }
}

.error {
  color: $c-danger;
  font-size: 0.75rem;
}

.action {
  height: inherit;
  align-content: center;
  justify-self: center;
}

.toolbar {
  $sidebar-width: 340px;
  :deep(.toolbar__options) {
    height: 100%;
    display: flex;
    align-items: center;
    > .menu-activator {
      height: 100%;
      width: auto;
      aspect-ratio: 1 / 1;
      border-radius: 0;
    }
  }
}
</style>
